import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CategoryType } from "../models/finance/CategoryType";
import type Category from "../models/finance/Category";
import { getCategories, createCategory, updateCategory, deleteCategory } from "../api/category";
import { expenseKeys } from "./expense";
import { incomeKeys } from "./income";

const categoryKeys = {
  all: ["categories"] as const,
  list: (type: CategoryType, includeInactive: boolean) => [...categoryKeys.all, type, includeInactive] as const,
};

export function useCategoriesQuery(type: CategoryType, includeInactive: boolean = false) {
  return useQuery({
    queryKey: categoryKeys.list(type, includeInactive),
    queryFn: () => getCategories(type, includeInactive),
    staleTime: 60_000,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ title, type }: { title: string; type: CategoryType }) =>
      createCategory(title, type),
    onSuccess: (_created, variables) => {
      qc.invalidateQueries({ queryKey: categoryKeys.list(variables.type, false) });
      qc.invalidateQueries({ queryKey: categoryKeys.list(variables.type, true) });
    },
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, newTitle, newIsActive }: { category: Category; newTitle?: string; newIsActive?: boolean }) =>
      updateCategory(category, newTitle, newIsActive),
    onSuccess: (_updated, variables) => {
      const derivedType = variables.category.type === "expense" ? CategoryType.Expense : CategoryType.Income;
      qc.invalidateQueries({ queryKey: categoryKeys.list(derivedType, false) });
      qc.invalidateQueries({ queryKey: categoryKeys.list(derivedType, true) });
    },
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ category, fallbackCategoryId }: { category: Category; fallbackCategoryId?: string }) =>
      deleteCategory(category, fallbackCategoryId),
    onSuccess: (_void, variables) => {
      const derivedType = variables.category.type === "expense" ? CategoryType.Expense : CategoryType.Income;
      
      // Invalidate categories cache
      qc.invalidateQueries({ queryKey: categoryKeys.list(derivedType, false) });
      qc.invalidateQueries({ queryKey: categoryKeys.list(derivedType, true) });
      
      // Invalidate expenses/incomes cache because items were reassigned to fallback category
      if (derivedType === CategoryType.Expense) {
        qc.invalidateQueries({ queryKey: expenseKeys.all as unknown as string[] });
      } else {
        qc.invalidateQueries({ queryKey: incomeKeys.all as unknown as string[] });
      }
    },
  });
}


